import moment from 'moment';

export type CalendarExporterType = 'google' | 'yahoo' | 'outlook' | 'vCalendar' | 'iCalendar';

export interface CalendarExporter {
  readonly type: CalendarExporterType;
  readonly label: string;
  readonly icon: string;
}

export interface CalendarEvent {
  readonly start?: string;
  readonly end?: string;
  readonly title?: string;
  readonly details?: string;
  readonly location?: string;
  readonly description?: string;
  readonly retrieve?: string;
  readonly retrieveMarkup?: string;
  readonly url?: string;
}

export abstract class CalendarExporterAbstract {
  protected readonly DATE_FORMAT_URL = 'YYYYMMDD';
  protected readonly DATE_FORMAT = 'YYYY-MM-DD';

  abstract exportForProject(releaseDate: string, projectCode: string): void;

  abstract exportForPublication(releaseDate: string, bibCode: string): void;

  abstract exportForObservation(releaseDate: string, datasetId: string): void;

  protected getCalendarEventForProjectCode(releaseDate: string, projectCode: string): CalendarEvent {
    const calendarEvent = this.getCalendarEvent(releaseDate);

    const title = `Project ${projectCode} expired`;
    const description = `The proprietary period of some or all data from Project ${projectCode} expires at ${calendarEvent.start}.`;
    const details = `${description} ${calendarEvent.retrieve}`;

    return {...calendarEvent, title, description, details};
  }

  protected getCalendarEventForBibCode(releaseDate: string, bibCode: string): CalendarEvent {
    const calendarEvent = this.getCalendarEvent(releaseDate);

    const title = `Publication ${bibCode} expired`;
    const description = `The proprietary period of some or all data from Publication ${bibCode} expires at ${calendarEvent.start}.`;
    const details = `${description} ${calendarEvent.retrieve}`;

    return {...calendarEvent, title, description, details};
  }

  protected getCalendarEventForObservation(releaseDate: string, datasetId: string): CalendarEvent {
    const calendarEvent = this.getCalendarEvent(releaseDate);

    const title = `Observation ${datasetId} expired`;
    const description = `The proprietary period of Observation ${datasetId} expires at ${calendarEvent.start}.`;
    const details = `${description} ${calendarEvent.retrieve}`;

    return {...calendarEvent, title, description, details};
  }

  private getCalendarEvent(releaseDate: string): CalendarEvent {
    const start = moment(releaseDate, this.DATE_FORMAT).add(1, 'days').format(this.DATE_FORMAT_URL);
    const end = moment(releaseDate, this.DATE_FORMAT).add(2, 'days').format(this.DATE_FORMAT_URL);
    const location = 'ALMA';
    const url = window.location.href;
    const retrieve = `Those data can be accessed through this URL: ${url}.`;
    const retrieveMarkup = encodeURI(`Those data can be accessed through the URL.`);
    return {start, end, location, url, retrieve, retrieveMarkup};
  }
}
